import * as React from "react";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { AllIconNames, FxInput as BaseFxInput, PopupPositionsType } from "react-ui";

import { toHtmlElementOrNull } from "../../Commons/DOM/DomHelpers";
import { themed } from "../../Internals/ThemeProvider/ThemedWorkaround";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { ExcelPasteUtils } from "../../MultiControls/MultiLine/ExcelPasteUtils";
import {
    DataPasteCallback,
    DataPasteContext,
    DatePasteStatus,
} from "../../MultiControls/MultiLine/TableDataPasteContext";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { isScreenshotTestingMode } from "../../Testing";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import {
    fixDecimalComma,
    fixDecimalValue,
    fixNumberValue,
    formatValue,
    wrapWithLimitedByRegexp,
} from "../Input/InputHelpers";
import { GrayWrapper, hideCursor, highlighted, ReadOnlyTextWrapper } from "../Input/Input.styles";
import { renderPrefix, renderSuffix } from "../Input/RenderAffix";
import { InputCommonProps, ReadOnlyInputText } from "../Input/Input";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { emptyRef } from "../../TypingUtils";
import { isNotNullOrEmpty } from "../../../../../Common/TypingUtils";
import { tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";

import { ReadOnlyAlignFxInput } from "./FxInput.styles";
import { WithFxTooltip } from "./FxInput.utils";
import { FxInputTestPageObject } from "./FxInputTestPageObject";

export interface FxInputProps extends ControlTooltipProps, ControlValidationProps, InputCommonProps {
    autoFlag: boolean;
    placeholder?: string;
    autoValue: undefined | string;
    formula?: React.ReactNode;
    hint?: React.ReactNode;
    setValue: (value: undefined | string) => void;
    tooltipPosition?: PopupPositionsType;
    highlightComponents: () => void;
    unhighlightComponents: () => void;
    focusByEnterOnNextControl: (event: React.KeyboardEvent) => void;
    verticalAlign?: boolean;
    useAutoIcon?: AllIconNames;
    onClick?: () => void;
}

export function FxInput(props: FxInputProps): JSX.Element {
    const [focused, setFocused] = useState(false);
    const inputRef = React.useRef<BaseFxInput>(emptyRef);
    const readOnlyTextWrapper = React.useRef<HTMLDivElement>(emptyRef);
    const inputDOMElement = useMemo(
        () => toHtmlElementOrNull(ReactDOM.findDOMNode(inputRef.current)),
        [inputRef.current]
    );

    const setValue = wrapWithLimitedByRegexp(props.setValue, props.inputLimit, blink);
    const setValueWithoutBlink = wrapWithLimitedByRegexp(props.setValue, props.inputLimit);

    const highlightingType = props.externalHighlightingOptions
        ? props.externalHighlightingOptions.highlightingType
        : undefined;

    useLayoutEffect(() => {
        if (props.withIgnoreAutoFlag && props.ignoreAutoFlag) {
            props.setIgnoreAutoFlag(false);
        }
    }, []);

    useEffect(() => {
        if (!props.disabled) {
            if (props.dataType === "decimal" || props.dataType === "number") {
                const fixedValue = fixDecimalValue(props.value);
                const fixedAutoValue = fixDecimalValue(props.autoValue);
                if (isNotNullOrEmpty(fixedValue) && fixedValue === fixedAutoValue) {
                    setValue(fixedValue, props.value);
                }
            }
        }
    }, [props.disabled]);

    const handleRequestFocus = () => {
        if (inputRef.current != undefined) {
            inputDOMElement?.scroll();
            inputRef.current.focus();
        }
        if (props.isReadOnly && readOnlyTextWrapper.current) {
            tryScrollIntoViewWithFallback(readOnlyTextWrapper.current, {
                block: "center",
                inline: "center",
                behavior: "smooth",
            });
        }
    };

    function blink() {
        inputRef.current?.blink();
    }

    const handleFocus = () => {
        setFocused(true);
        props.metrics?.saveValue(props.value);
    };

    const handleChange = (value: null | undefined | string, force?: boolean) => {
        if (shouldPutEmptyPlaceholder() && !force) {
            return;
        }
        if (props.dataType === "decimal") {
            setValue(fixDecimalComma(value), props.value);
        } else {
            setValue(value ?? "", props.value);
        }
    };

    const handlePasteHandler = (
        onPaste: DataPasteCallback
    ): ((event: React.ClipboardEvent<unknown>) => DatePasteStatus) => {
        if (props.currency) {
            return event => onPaste(event.clipboardData, { bindingPath: props.bindingPath });
        }
        return ExcelPasteUtils.WrapHandlePaste(onPaste, props.bindingPath);
    };

    const getValue = () => (shouldPutEmptyPlaceholder() ? "—" : props.value);

    const handleRestore = () => props.setValue(props.autoValue);

    const handleBlur = () => {
        setFocused(false);
        props.metrics?.sendValue(props.bindingPath, props.value);

        if (props.dataType === "number") {
            setValueWithoutBlink(fixNumberValue(props.value), props.value);
        }

        if (props.dataType === "decimal") {
            setValueWithoutBlink(fixDecimalValue(props.value), props.value);
        }
    };

    const shouldPutEmptyPlaceholder = () =>
        !focused && props.autoFlag && (props.value == undefined || props.value === "") && !props.currency;

    const handleOnMouseEnter = () => {
        props.highlightComponents();
    };

    const handleOnMouseLeave = () => {
        props.unhighlightComponents();
    };

    useTestPageObject(
        () =>
            FxInputTestPageObject(
                getValue,
                props.autoFlag,
                props.disabled,
                handleRestore,
                handleChange,
                inputDOMElement
            ),
        [inputDOMElement, props.value, handleChange, handleFocus, handleRestore]
    );

    if (props.isReadOnly) {
        return (
            <WithFxTooltip {...props}>
                <ReadOnlyTextWrapper
                    width={props.width}
                    removeBackground={props.removeReadonlyBackground}
                    noPadding={props.noReadonlyPadding}
                    highlightingType={highlightingType}
                    ref={readOnlyTextWrapper}>
                    {props.prefix === "(" && <GrayWrapper>{renderPrefix(props.prefix)}</GrayWrapper>}
                    <ReadOnlyAlignFxInput align={props.align} autoFlag={props.autoFlag}>
                        <ReadOnlyInputText {...props} />
                    </ReadOnlyAlignFxInput>
                    {props.suffix && <GrayWrapper>{renderSuffix(props.suffix)}</GrayWrapper>}
                </ReadOnlyTextWrapper>
            </WithFxTooltip>
        );
    }

    return (
        <WithFxTooltip {...props}>
            <FocusManagementObserver onRequestFocus={handleRequestFocus}>
                <DataPasteContext.Consumer>
                    {handlePaste => (
                        <BaseFxInput
                            css={themed([props.highlighted && highlighted, isScreenshotTestingMode() && hideCursor])}
                            suffix={renderSuffix(props.suffix)}
                            prefix={props.prefix}
                            useAutoIcon={props.useAutoIcon}
                            data-tid={props["data-tid"]}
                            signed
                            type={props.currency ? "currency" : undefined}
                            integerDigits={props.currencyIntegerDigits}
                            fractionDigits={props.currencyFractionDigits}
                            auto={props.autoFlag}
                            disabled={props.disabled}
                            error={Boolean(props.error)}
                            ref={inputRef}
                            placeholder={props.placeholder}
                            value={props.currency || shouldPutEmptyPlaceholder() ? getValue() : formatValue(props)}
                            warning={Boolean(props.warning)}
                            width={props.width}
                            showNegativeAsBrackets={props.showNegativeAsBrackets}
                            onBlur={handleBlur}
                            onValueChange={handleChange}
                            onFocus={handleFocus}
                            onMouseEnter={handleOnMouseEnter}
                            onMouseLeave={handleOnMouseLeave}
                            onRestore={handleRestore}
                            align={props.align ?? "right"}
                            onKeyDown={props.focusByEnterOnNextControl}
                            onPaste={handlePasteHandler(handlePaste)}
                            selectAllOnFocus={
                                props.dataType === "decimal" || props.dataType === "number" || props.currency
                            }
                        />
                    )}
                </DataPasteContext.Consumer>
            </FocusManagementObserver>
        </WithFxTooltip>
    );
}
